import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';

/**
 * FormDrawer
 * --------------------------------------------------
 * A modern right-side slide-in drawer used for forms
 * (create/edit). Handles:
 *   - Portal render into document.body
 *   - Esc / backdrop click to close
 *   - Body scroll lock while open
 *   - Auto-focus first focusable element on open
 *   - Respects prefers-reduced-motion
 *
 * Props:
 *   open            boolean
 *   onClose         () => void
 *   title           string
 *   subtitle        string (optional)
 *   icon            ReactNode – icon element rendered inside the accent pill
 *   iconColor       'primary' | 'violet' (default 'primary')
 *   footer          ReactNode – rendered inside sticky footer
 *   widthClass      string – override max-width (default 'sm:max-w-xl lg:max-w-2xl')
 *   children        form body
 */
const iconColorMap = {
  primary: 'bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-300',
  violet: 'bg-violet-50 text-violet-600 dark:bg-violet-900/30 dark:text-violet-300',
};

const FormDrawer = ({
  open,
  onClose,
  title,
  subtitle,
  icon,
  iconColor = 'primary',
  footer,
  widthClass = 'sm:max-w-xl lg:max-w-2xl',
  children,
}) => {
  const panelRef = useRef(null);

  // Esc to close + body scroll lock
  useEffect(() => {
    if (!open) return undefined;

    const handleKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose?.();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKey);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, onClose]);

  // Auto-focus first form element when drawer opens
  useEffect(() => {
    if (!open || !panelRef.current) return;
    const raf = requestAnimationFrame(() => {
      const first = panelRef.current?.querySelector(
        'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled])'
      );
      if (first && typeof first.focus === 'function') {
        try {
          first.focus({ preventScroll: true });
        } catch (_) {
          first.focus();
        }
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [open]);

  if (!open) return null;

  const accent = iconColorMap[iconColor] || iconColorMap.primary;

  const drawer = (
    <div className="fixed inset-0 z-[100]" aria-hidden={!open}>
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-secondary-900/50 backdrop-blur-sm animate-fade-in motion-reduce:animate-none"
        onClick={onClose}
      />

      {/* Panel */}
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`absolute right-0 top-0 h-full w-full ${widthClass} bg-white dark:bg-secondary-900 shadow-2xl border-l border-secondary-200 dark:border-secondary-700 flex flex-col animate-slide-in-right motion-reduce:animate-fade-in`}
      >
        {/* Header */}
        <header className="flex items-start gap-3 px-6 py-5 border-b border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-900">
          {icon && (
            <div className={`h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 ${accent}`}>
              {icon}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-secondary-900 dark:text-white truncate">
              {title}
            </h2>
            {subtitle && (
              <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-0.5 truncate">
                {subtitle}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-2 rounded-lg text-secondary-500 hover:text-secondary-900 hover:bg-secondary-100 dark:text-secondary-400 dark:hover:text-white dark:hover:bg-secondary-800 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-secondary-900"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-6 bg-secondary-50/40 dark:bg-secondary-900/40">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <footer className="px-6 py-4 border-t border-secondary-200 dark:border-secondary-700 bg-white/90 dark:bg-secondary-900/90 backdrop-blur flex items-center justify-end gap-2 flex-shrink-0">
            {footer}
          </footer>
        )}
      </aside>
    </div>
  );

  return ReactDOM.createPortal(drawer, document.body);
};

export default FormDrawer;

/**
 * FormSection
 * A thin, titled section used inside FormDrawer bodies to
 * keep consistent spacing + typography across all drawers.
 */
export const FormSection = ({ title, description, children, className = '' }) => (
  <section className={`pb-6 mb-6 border-b border-secondary-200/70 dark:border-secondary-700/70 last:border-b-0 last:mb-0 last:pb-0 ${className}`}>
    {(title || description) && (
      <div className="mb-4">
        {title && (
          <h3 className="text-xs font-semibold uppercase tracking-wide text-secondary-500 dark:text-secondary-400">
            {title}
          </h3>
        )}
        {description && (
          <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1">
            {description}
          </p>
        )}
      </div>
    )}
    <div className="space-y-4">{children}</div>
  </section>
);
